import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, Reveal, Label } from "@/components/common/Reveal";
import { CtaButton } from "@/components/common/Cta";
import { ApplicationForm } from "@/components/forms/ApplicationForm";
import { lockScroll } from "@/components/layout/SmoothScroll";
import { ROLES, CONTACT } from "@/data/site";

const RoleCard = ({ role, i, onApply }) => (
  <Reveal>
    <article className="grid gap-10 border-t border-canvas-line py-14 lg:grid-cols-12" data-testid={`role-${role.id}`}>
      <div className="lg:col-span-4">
        <span className="label-mono text-brand">Role {String(i + 1).padStart(2, "0")} · Now hiring</span>
        <h2 className="mt-4 font-display text-4xl font-bold uppercase leading-none sm:text-5xl">{role.title}</h2>
        <dl className="mt-6 space-y-1 text-sm text-steel">
          <div><dt className="inline text-ink">Type: </dt><dd className="inline">{role.type}</dd></div>
          <div><dt className="inline text-ink">Location: </dt><dd className="inline">{role.location}</dd></div>
          <div><dt className="inline text-ink">Schedule: </dt><dd className="inline">{role.schedule}</dd></div>
        </dl>
        <CtaButton onClick={() => onApply(role.title)} className="mt-8" testid={`apply-${role.id}-btn`}>Apply for this role</CtaButton>
      </div>
      <div className="lg:col-span-7 lg:col-start-6">
        <p className="text-base leading-relaxed text-ink/90 sm:text-lg">{role.intro}</p>
        <div className="mt-8 grid gap-8 md:grid-cols-2">
          {role.lists.map((l) => (
            <div key={l.title}>
              <h3 className="label-mono text-steel">{l.title}</h3>
              <ul className="mt-4 space-y-2 text-sm leading-relaxed text-steel">
                {l.items.map((it) => <li key={it} className="flex gap-3"><span className="text-brand">—</span>{it}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </article>
  </Reveal>
);

export default function Careers() {
  const [role, setRole] = useState(null);
  const change = (open) => {
    lockScroll(open);
    if (!open) setRole(null);
  };
  const apply = (title) => {
    lockScroll(true);
    setRole(title);
  };

  return (
    <main data-testid="careers-page">
      <PageHeader testid="careers-header" label="Careers" title="A career instead of a job?"
        text="Tired of your current position? Looking for an opportunity to grow and develop? We're committed to bringing out the very best in everyone who works with us, on residential schemes across Dublin and surrounding counties." />
      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
        {ROLES.map((r, i) => <RoleCard key={r.id} role={r} i={i} onApply={apply} />)}
        <div className="border-t border-canvas-line pt-10 text-steel">
          <Label>Prefer email?</Label>
          <p className="mt-4">Send your CV to <a href={`mailto:${CONTACT.recruitmentEmail}`} className="text-ink hover:text-brand" data-testid="careers-email-link">{CONTACT.recruitmentEmail}</a> or call our office on {CONTACT.phones[0].value}.</p>
        </div>
      </section>
      <Dialog open={!!role} onOpenChange={change}>
        <DialogContent data-lenis-prevent className="max-h-[92vh] overflow-y-auto rounded-none border-canvas-line bg-canvas-800 p-6 text-ink sm:max-w-2xl sm:p-10" data-testid="application-dialog">
          <DialogTitle className="font-display text-3xl font-bold uppercase">Apply — {role}</DialogTitle>
          <DialogDescription className="text-steel">Fill in your details and our recruitment team will be in touch.</DialogDescription>
          {role && <ApplicationForm key={role} defaultRole={role} />}
        </DialogContent>
      </Dialog>
    </main>
  );
}
